import * as Sentry from "@sentry/browser";
import { Button, Col, message, Row, Spin } from "antd";
import { FormInstance } from "antd/lib/form";
import { AxiosResponse } from "axios";
import debounce from "lodash.debounce";
import React, { Fragment, RefObject } from "react";
import { RouteComponentProps } from "react-router";
import Card from "../components/Card";
import BaseForm from "../components/Form/BaseForm";
import ModalCreator from "../components/ModalCreator";
import { GET_SALES_PEOPLE } from "../gql/users";
import {
  Company,
  Customer,
  FormFieldConfig,
  OpportunityProspect,
  Product,
  ProductWithSubscriptionId,
  SelectOptionsInterface,
  Subscription,
  User,
} from "../helpers/shared-interfaces";
import {
  convertCustomersToSelectOptions,
  generateDisplayErrorMessage,
  handleValidationErrors,
  injectKey,
  parseTransactionFields,
  request,
  responseHandler,
} from "../helpers/utilities";
import { GET_OPPORTUNITY_FORM_DATA } from "../gql/opportunity";
import { contributionTypes, DEBOUNCE_TIME } from "../helpers/constants";

interface State {
  isLoading: boolean;
  products: Product[];
  companies: Company[];
  customers: Customer[];
  searchTerm: string;
  isSearching: boolean;
  selectedCustomer?: Customer;
  selectedCompany?: Company;
  selectedProduct?: ProductWithSubscriptionId;
  showConfirmCreateModal: boolean;
  salesPersons: User[];
  showCancelRequest: boolean;
  salesPersonSearchTerm: string;
  opportunityProspects: OpportunityProspect[];
}

interface RouteParams {
  id?: string;
}

export interface Props extends RouteComponentProps<RouteParams> {
  name?: string;
  user: User;
  customer?: Customer;
  company?: Company;
  handleSuccess?: () => void;
}

export const CREATE_CUSTOMER_SUBSCRIPTION = `
 mutation CreateCustomerSubscription($productId: ID!, $customerId: ID!, $salesPersonId: ID){
    createCustomerSubscription(productId: $productId, customerId: $customerId, salesPersonId: $salesPersonId ) {
    customerSubscription {
      id
      product{
        id
        name
        isActive
      }
      salesPerson{
        id
        name
      }
    }
    errors{
      description
      property
    }
  }
 }
`;

export const CREATE_COMPANY_SUBSCRIPTION = `
 mutation CreateCompanySubscription($productId: ID!, $companyId: ID!, $salesPersonId: ID, $contributionAllocationTypes: [String!]){
    createCompanySubscription(productId: $productId, companyId: $companyId, salesPersonId: $salesPersonId, contributionAllocationTypes: $contributionAllocationTypes ) {
        companySubscription {
        id
        product{
            id
            name
            isActive
        }
        salesPerson{
            id
            name
        }
        contributionAllocationTypes
        }
        errors{
            description
            property
        }
    }
 }
`;

export const GET_PRODUCTS = `
query($page:ID!) {
  products(page: $page) {
    count
    results{
      id
      name
      productTier {
        id
        name
      }
      productCategory {
        id
        name
      }
      opportunityProspect {
        id
        name
      }
    }
    errors{
      description
    }
  }
}
`;

export const SEARCH_CUSTOMER = `
query SearchCustomer($q: String!, $page: ID!) {
  customerSearch(q: $q, page: $page) {
    count
    results {
      id
      petraId
      firstName
      middleName
      lastName
      activeCustomerPhones {
        id,
        phone {
          id
          number
          type
        }
        isPrimary
      }
      customerEmails {
        id
        isPrimary
        email {
          id
          address
        }
      }
      customerSubscriptions {
        id
        product {
          id
          name
          isActive
          fundCode
          taxWaiverRequired
          productTier {
            id
            name
          }
          productCategory {
            id
            name
          }
          assetMapId
        }
        productDuration
        customerLiens {
          id
          currentAmount
          totalAmount
          loanPaid
          provider
          employeePreTax
          employeePostTax
          employerPreTax
          employerPostTax
          startDate
          endDate
        }
      }
      customerPayoutAccounts {
        id
        accountNumber
        name
        accountType
        institutionName
        bankBranch {
          id
          name
          financialInstitution {
            id
            name
          }
        }
      }
      customerCompanyAssociations {
        company {
          id
          name
        }
      }
      employers {
        id
        name
        industrySector {
          name
        }
        petraId
        tinNumber
      }
      formerCompanies {
        id
        name
        industrySector {
          name
        }
        petraId
        tinNumber
      }
    }
    errors {
      property
      description
    }
  }
}
`;

const fieldsToBeValidated = ["productId", "salesPersonId"];

const savingsBoosterContributionAllocationTypes = [
  "employee_pre_tax",
  "employee_post_tax",
];

function isSavingsBoosterProduct(productName?: string): boolean {
  return /saving[s]?[-\s]+booster/i.test(productName ?? "");
}

function filterContributionAllocationTypeOptions(
  options: SelectOptionsInterface[],
  productName?: string,
): SelectOptionsInterface[] {
  if (!isSavingsBoosterProduct(productName)) {
    return options;
  }

  return options.filter((type) =>
    savingsBoosterContributionAllocationTypes.includes(type.id),
  );
}

function getContributionAllocationTypeOptions(
  productName?: string,
): SelectOptionsInterface[] {
  return filterContributionAllocationTypeOptions(
    contributionTypes,
    productName,
  );
}

function sanitizeContributionAllocationTypes(
  values: string[] = [],
  productName?: string,
): string[] {
  const allowedTypes = new Set(
    getContributionAllocationTypeOptions(productName).map(({ id }) => id),
  );

  return values.filter((value) => allowedTypes.has(value));
}

export default class CreateCustomerSubscription extends React.Component<
  Props,
  State
> {
  reference: RefObject<FormInstance>;
  attachmentsReference: RefObject<FormInstance>;

  constructor(props: Props) {
    super(props);
    this.state = {
      isLoading: false,
      products: [],
      companies: [],
      customers: [],
      searchTerm: "",
      isSearching: false,
      selectedCustomer: undefined,
      selectedProduct: undefined,
      showConfirmCreateModal: false,
      salesPersons: [],
      showCancelRequest: false,
      salesPersonSearchTerm: "",
      opportunityProspects: [],
    };

    this.reference = React.createRef<FormInstance>();
    this.attachmentsReference = React.createRef<FormInstance>();
  }

  componentDidMount(): void {
    if (this.props.customer || this.props.company) {
      this.setInitialSelectedCustomerOrCompany();
    }
    this.getOpportunityCreationData();
    this.getAllSalesPerson();
  }

  handleSalesPersonSearchInput = async (
    salesPersonSearchTerm: string,
  ): Promise<void> => {
    this.setState({ salesPersonSearchTerm }, () => {
      this.triggerSalesPersonDelayedSearch();
    });
  };

  triggerSalesPersonDelayedSearch = debounce(async (): Promise<void> => {
    this.getAllSalesPerson();
  }, DEBOUNCE_TIME);

  handleProductChange = (productId: string): void => {
    const allProducts: ProductWithSubscriptionId[] =
      this.state.opportunityProspects
        .map((prospect) => prospect.products)
        .flat() as ProductWithSubscriptionId[];
    const selectedProduct = allProducts.find(
      (product) => product.id === productId,
    );

    this.setState(
      { selectedProduct: selectedProduct as ProductWithSubscriptionId },
      () => {
        if (selectedProduct?.transactionFields) {
          try {
            const transactionFields = parseTransactionFields(
              selectedProduct.transactionFields,
            );
            const contributionKeys = sanitizeContributionAllocationTypes(
              Object.keys(transactionFields),
              selectedProduct.name,
            );

            if (this.reference.current) {
              this.reference.current.setFieldsValue({
                contributionAllocationTypes: contributionKeys,
              });
            }
          } catch (error) {
            console.error("Error parsing transactionFields:", error);
          }
        } else {
          if (this.reference.current) {
            this.reference.current.setFieldsValue({
              contributionAllocationTypes: [],
            });
          }
        }
      },
    );
  };

  getContributionOptionsFromProduct = (): SelectOptionsInterface[] => {
    const { selectedProduct } = this.state;
    if (!selectedProduct?.transactionFields) {
      return getContributionAllocationTypeOptions(selectedProduct?.name);
    }

    try {
      const transactionFields = parseTransactionFields(
        selectedProduct.transactionFields,
      );
      return filterContributionAllocationTypeOptions(
        Object.entries(transactionFields).map(([id, name]) => ({ id, name })),
        selectedProduct.name,
      );
    } catch (error) {
      console.error("Error parsing transactionFields:", error);
      return getContributionAllocationTypeOptions(selectedProduct?.name);
    }
  };

  toggleModal = (): void => {
    this.setState({
      showConfirmCreateModal: false,
    });
  };

  toggleConfirmCreateModal = (): void => {
    this.setState({
      showConfirmCreateModal: true,
      isLoading: false,
    });
  };

  setRef = (
    name: "reference" | "attachmentsReference",
    ref: React.RefObject<FormInstance>,
  ): void => {
    this[name] = ref;
  };

  setInitialSelectedCustomerOrCompany = (): void => {
    if (this.props.company) {
      this.setState({ selectedCompany: this.props.company });
    } else {
      this.setState({
        selectedCustomer: this.props.customer,
      });
    }
  };

  getAllSalesPerson = (): void => {
    request({
      query: GET_SALES_PEOPLE,
      variables: {
        page: 1,
        q: this.state.salesPersonSearchTerm,
      },
    }).then(
      ({
        data: { data },
      }: {
        data: {
          data: {
            salesPeople: {
              results: User[];
            };
          };
        };
      }): void => {
        this.setState({
          salesPersons: data.salesPeople.results,
          isLoading: false,
        });
      },
    );
  };

  createCustomerSubscription = async (): Promise<void> => {
    interface Response {
      customerSubscription: Subscription;
      companySubscription: Subscription;
      errors: {
        description: string;
        property: string;
      };
    }

    if (!this.reference.current) return;

    let data;

    try {
      const modifiedFieldsToBeValidated = [
        ...fieldsToBeValidated,
        this.state.selectedCustomer ? "customerId" : "companyId",
        ...(this.state.selectedCustomer ? [] : ["contributionAllocationTypes"]),
      ];
      data = await this.reference.current.validateFields(
        modifiedFieldsToBeValidated,
      );

      if (!this.state.selectedCustomer) {
        data = {
          ...data,
          contributionAllocationTypes: sanitizeContributionAllocationTypes(
            data.contributionAllocationTypes,
            this.state.selectedProduct?.name,
          ),
        };
      }
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    this.setState({ isLoading: true, showConfirmCreateModal: false });

    try {
      const response: AxiosResponse = await request({
        query: this.state.selectedCustomer
          ? CREATE_CUSTOMER_SUBSCRIPTION
          : CREATE_COMPANY_SUBSCRIPTION,
        variables: data,
      });

      const mutationKey = this.state.selectedCustomer
        ? "createCustomerSubscription"
        : "createCompanySubscription";

      const { err, mutation } = responseHandler<Response>(
        response,
        mutationKey,
      );

      if (err) {
        message.error(generateDisplayErrorMessage(err));
      }

      const subscription = this.state.selectedCustomer
        ? mutation.customerSubscription
        : mutation.companySubscription;

      if (subscription) {
        message.success("Product added");
        this.reference.current.resetFields();
        this.props.handleSuccess && this.props.handleSuccess();
      }
    } catch (error) {
      message.error(`failed to add product - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  getOpportunityCreationData(): void {
    request({
      query: GET_OPPORTUNITY_FORM_DATA,
    }).then(
      (response: {
        data: {
          data: {
            opportunityProspects: OpportunityProspect[];
          };
        };
      }): void => {
        this.setState({
          opportunityProspects: response.data.data.opportunityProspects,
        });
      },
    );
  }

  renderLabel(required: boolean, name: string): JSX.Element {
    return (
      <label>
        {required ? (
          <span>
            <span style={{ color: "red" }}>*</span> {name}
          </span>
        ) : (
          name
        )}
      </label>
    );
  }

  toggleCancelModal = async (): Promise<void> => {
    this.setState({ showCancelRequest: !this.state.showCancelRequest });
  };

  cancelRequest = async (): Promise<void> => {
    const backLocation = window.location.pathname.replace("/new", "");
    window.location.assign(backLocation);
  };

  render(): JSX.Element {
    const {
      isLoading,
      isSearching,
      customers,
      companies,
      selectedCustomer,
      selectedCompany,
      selectedProduct,
      showConfirmCreateModal,
      opportunityProspects,
      salesPersons,
    } = this.state;

    const modifiedProducts: SelectOptionsInterface[] = opportunityProspects
      .map((prospect) => prospect.products)
      .flat();

    let modifiedCompanies: SelectOptionsInterface[] = injectKey<Company>(
      companies,
      "name",
    );
    if (selectedCompany) {
      modifiedCompanies = [
        ...modifiedCompanies,
        { id: selectedCompany?.id, name: selectedCompany?.name },
      ];
    }

    const fieldsToHide = selectedCustomer
      ? ["companyId", "contributionAllocationTypes"]
      : ["customerId"];

    const config: FormFieldConfig[] = [
      {
        id: "customerId",
        name: "customerId",
        inputType: "search",
        required: true,
        placeholder: "Search by name or petra id",
        rule: "string",
        label: "Customer",
        disabled: true,
        selectOptions: selectedCustomer
          ? convertCustomersToSelectOptions([selectedCustomer])
          : convertCustomersToSelectOptions(customers),
        initialValue: selectedCustomer?.id || this.props.customer?.id,
        colConfig: {
          span: 24,
        },
      },
      {
        id: "companyId",
        name: "companyId",
        inputType: "select",
        required: true,
        disabled: true,
        placeholder: `Select company`,
        rule: "string",
        label: "Company",
        selectOptions: modifiedCompanies,
        initialValue: selectedCompany?.id || this.props.company?.id,
        colConfig: {
          span: 24,
        },
      },
      {
        id: "productId",
        name: "productId",
        rule: "string",
        inputType: "select",
        required: true,
        label: "Product",
        placeholder: "Select Product",
        selectOptions: modifiedProducts,
        changeHandler: this.handleProductChange,
        colConfig: {
          span: 24,
        },
      },
      {
        id: "salesPersonId",
        name: "salesPersonId",
        rule: "string",
        inputType: "search",
        required: true,
        label: "Sales Person",
        placeholder: "Select sales person",
        selectOptions: salesPersons,
        changeHandler: this.handleSalesPersonSearchInput,
        isSearching,
        colConfig: {
          span: 24,
        },
      },
      {
        id: `contributionAllocationTypes-${selectedProduct?.id || "default"}`,
        name: "contributionAllocationTypes",
        placeholder: "Select contribution types...",
        required: false,
        inputType: "multipleSelect",
        label: "Contribution Allocation Types",
        selectOptions: this.getContributionOptionsFromProduct(),
        colConfig: {
          span: 24,
        },
      },
    ];

    return (
      <Fragment>
        {showConfirmCreateModal &&
          ModalCreator({
            title: "Create Product Subscription",
            okType: "primary",
            okText: "Create",
            showModal: showConfirmCreateModal,
            onOkay: this.createCustomerSubscription,
            toggleModal: this.toggleModal,
            inscription: "Are you sure you want to add product ?",
          })}
        <Spin spinning={isLoading}>
          <Card header="New Subscription">
            <Row>
              <Col span={8}>
                <BaseForm
                  reference={this.reference}
                  fieldsToHide={fieldsToHide}
                  layout="vertical"
                  fields={config}
                />
                <div>
                  <Button
                    type="primary"
                    onClick={this.toggleConfirmCreateModal}
                    className="base-button"
                    style={{
                      position: "relative",
                      top: "10px",
                      right: "5px",
                      borderRadius: 0,
                      height: "33px",
                    }}
                  >
                    Add Product
                  </Button>
                  <Button
                    type="primary"
                    onClick={this.toggleCancelModal}
                    className="base-button"
                    style={{
                      position: "relative",
                      top: "10px",
                      borderRadius: 0,
                      height: "33px",
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </Col>

              <div>
                {this.state.showCancelRequest &&
                  ModalCreator({
                    title: "Cancel",
                    okType: "primary",
                    okText: "Yes",
                    width: 400,
                    showModal: true,
                    onOkay: this.cancelRequest,
                    toggleModal: this.toggleCancelModal,
                    showFooter: true,
                    inscription: `Are you sure you want to cancel request ?`,
                  })}
              </div>
            </Row>
          </Card>
        </Spin>
      </Fragment>
    );
  }
}
